<template>
    <div class="apply-discount apply-discount form" novalidate>
        <div class="formrow">
            <VoucherInput
                :hasError="hasError"
                :showValidationIcon="false"
                v-model:value="voucherValue"
                :disabled="isApplying"
                :on-focus="onFocus"
                @submit="applyCode"
                @update:value="onChangeValue"
            />
        </div>

        <button type="button" class="apply-discount-btn" :disabled="isApplyButtonDisabled" @click="applyCode">
            <div class="icon">
                <i class="fas fa-check"></i>
            </div>
        </button>

        <button type="button" :disabled="isApplying" @click="scanQr" :hidden="!enableQrScanner">
            <div class="icon">
                <i class="fal fa-qrcode"></i>
            </div>
        </button>
    </div>
</template>

<script setup lang="ts">
    import { EnterDiscountOpenValuePopoverVue } from "@/app/popover/EnterDiscountOpenValuePopover";
    import { QrVoucherCodeScanner } from "@/components/vouchers/QrVoucherCodeScanner";
    import { Factory } from "@/data/dao/Factory";
    import { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import { DiscountsDao } from "@/data/dao/service/DiscountsDao";
    import type { IBill } from "@/data/model/Bill";
    import type { ICustomerCart } from "@/data/model/Customer";
    import { DiscountAccessMode, DiscountType } from "@/data/model/Enums";
    import type { IBillDiscountAppliedEventData, ICartDiscountAppliedEventData } from "@/data/model/Voucher";
    import { useBillStore } from "@/data/stores/bill";
    import { usePaymentStore } from "@/data/stores/payment";
    import { logError } from "@/shared/logging/errors";
    import { createEmptyDiv, createVueComponent } from "@/shared/utils/vue";
    import { computed, onMounted, ref } from "vue";

    import VoucherInput from "./VoucherInput.vue";
    import EnterDiscountOpenValueModal from "@/components/discount/EnterDiscountOpenValueModal.vue";

    const paymentStore = usePaymentStore();
    const billStore = useBillStore();
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    enum ApplyDiscountMode {
        Bill,
        Cart,
    }

    const props = defineProps<{
        inOperatorApp: boolean;
        enableQrScanner: boolean;
        locationId: string;
        cartId?: string | undefined;
        billId?: string | undefined;
        selectedMainBillItemIds?: string[] | undefined;
        onFocus?: { (hasFocus: boolean) };
        onApplyDiscountErrorOccurred?: (error: DaoError) => void;
        onDiscountAppliedToCart?: (eventData: ICartDiscountAppliedEventData) => void; // event handler to notify Discount Applied event to non-vue code -  we cannot use "emit" with non-vue components
        onDiscountAppliedToBill?: (eventData: IBillDiscountAppliedEventData) => void; // event handler to notify Discount Applied event to non-vue code -  we cannot use "emit" with non-vue components
    }>();

    const emit = defineEmits(["cartDiscountApplied", "billDiscountApplied", "applyDiscountErrorOccurred"]);

    const voucherValue = ref();
    const isValid = computed(() => voucherValue.value != null && voucherValue.value.length > 0);
    const isApplying = ref(false);
    const hasError = ref(false);
    const isApplyButtonDisabled = computed(() => !isValid.value || isApplying.value);
    const billId = ref<string | undefined>(props.billId);
    const cartId = ref<string | undefined>(props.cartId);
    const mode = computed(() => {
        if (cartId.value != null && billId.value != null) {
            throw Error("Provided Cart id and bill id provided at the same time is not supported");
        }

        if (cartId.value == null && billId.value == null) {
            throw Error("No cart id or bill id provided");
        }

        if (cartId.value != null && cartId.value.length > 0) {
            return ApplyDiscountMode.Cart;
        } else if (billId.value != null && billId.value.length > 0) {
            return ApplyDiscountMode.Bill;
        }
        return undefined;
    });

    const qrScanner = new QrVoucherCodeScanner();
    qrScanner.onCodeDetected = (code) => {
        voucherValue.value = code;
        applyCode();
    };

    onMounted(() => {
        reset();
    });

    async function scanQr() {
        try {
            await qrScanner.start();
        } catch (ex) {
            logError(ex);
        }
    }

    async function onChangeValue() {
        hasError.value = false;
    }

    async function applyCode() {
        hasError.value = false;
        const currentMode = mode.value;
        switch (currentMode) {
            case ApplyDiscountMode.Cart:
                if (cartId.value != null && cartId.value.length > 0) {
                    await applyCodeToCart(cartId.value);
                }
                break;
            case ApplyDiscountMode.Bill:
                if (billId.value != null && billId.value.length > 0) {
                    await applyCodeToBill(billId.value);
                }
                break;
            default:
                throw Error("Invalid mode" + mode.value);
        }
    }

    async function applyCodeToCart(cartId: string) {
        if (!isValid.value) return;

        try {
            const checkDiscountResult = await Factory.getInstance<DiscountsDao>(DiscountsDao).checkDiscountCode({
                code: voucherValue.value,
                locationId: props.locationId,
            });

            if (checkDiscountResult.isOpenValue) {
                if (props.inOperatorApp) {
                    openEnterDiscountOpenValuePopup(checkDiscountResult.discountType!, async (value) => {
                        await applyDiscountToCart(cartId, value);
                    });
                } else {
                    openEnterDiscountOpenValuePopover(checkDiscountResult.discountType!, async (value) => {
                        await applyDiscountToCart(cartId, value);
                    });
                }
            } else {
                await applyDiscountToCart(cartId, undefined);
            }
        } catch (ex) {
            logError(ex);
            const error = ex as DaoError;
            emit("applyDiscountErrorOccurred", error);
            hasError.value = true;
        } finally {
            isApplying.value = false;
        }
    }

    async function applyDiscountToCart(cartId: string, openValue: number | undefined) {
        try {
            const code = voucherValue.value;
            if (code == null) {
                return;
            }

            isApplying.value = true;

            const response = await customerDao.applyDiscountToCart({
                code: code,
                cartId: cartId,
                discountOpenValue: openValue,
                discountAccessMode: DiscountAccessMode.Code,
                selectedMainCartItemIds: undefined,
            });

            reset();
            triggerCartVoucherApplied(response);
        } catch (ex) {
            logError(ex);
            const error = ex as DaoError;
            emit("applyDiscountErrorOccurred", error);
            hasError.value = true;
        } finally {
            isApplying.value = false;
        }
    }

    async function applyCodeToBill(billId: string) {
        if (!isValid.value) return;

        try {
            const checkDiscountResult = await Factory.getInstance<DiscountsDao>(DiscountsDao).checkDiscountCode({
                code: voucherValue.value,
                locationId: props.locationId,
            });

            if (checkDiscountResult.isOpenValue) {
                if (props.inOperatorApp) {
                    openEnterDiscountOpenValuePopup(checkDiscountResult.discountType!, async (value) => {
                        await applyDiscountToBill(billId, value);
                    });
                } else {
                    openEnterDiscountOpenValuePopover(checkDiscountResult.discountType!, async (value) => {
                        await applyDiscountToBill(billId, value);
                    });
                }
            } else {
                await applyDiscountToBill(billId, undefined);
            }
        } catch (ex) {
            logError(ex);
            const error = ex as DaoError;
            emit("applyDiscountErrorOccurred", error);
            hasError.value = true;
        } finally {
            isApplying.value = false;
        }
    }

    function openEnterDiscountOpenValuePopup(discountType: DiscountType, onEnterValue: (value: number) => void) {
        const result = createVueComponent(
            EnterDiscountOpenValueModal,
            {
                onEnterValue: onEnterValue,
                discountType: discountType,
            },
            createEmptyDiv(),
            true
        );
        const popup = result.mountedComponent;
        popup.show();
    }

    function openEnterDiscountOpenValuePopover(discountType: DiscountType, onEnterValue: (value: number) => void) {
        const popover = new EnterDiscountOpenValuePopoverVue({
            onEnterValue: onEnterValue,
            discountType: discountType,
            uniqueId: "enter-discount-open-value"
        });
        popover.show();
    }

    async function applyDiscountToBill(billId: string, openValue: number | undefined) {
        try {
            isApplying.value = true;

            const items = billStore.selectedItemsList.map((item) => item.billItemId);
            const response = await customerDao.applyDiscountToBill({
                code: voucherValue.value,
                billId: billId,
                selectedMainBillItemIds: items,
                payUseCaseId: paymentStore.paymentService?.payUseCase?.id ?? "",
                discountAccessMode: DiscountAccessMode.Code,
                discountOpenValue: openValue,
            });

            if (response == null) {
                throw Error("No response from the server");
            }

            reset();
            triggerBillDiscountApplied(response);
        } catch (ex) {
            logError(ex);
            const error = ex as DaoError;
            emit("applyDiscountErrorOccurred", error);
            hasError.value = true;
        } finally {
            isApplying.value = false;
        }
    }

    function triggerCartVoucherApplied(response: ICustomerCart) {
        const eventData: ICartDiscountAppliedEventData = {
            cart: response,
        };

        if (props?.onDiscountAppliedToCart) {
            props?.onDiscountAppliedToCart(eventData);
        }
        emit("cartDiscountApplied", eventData);
    }

    function triggerBillDiscountApplied(response: IBill) {
        const eventData: IBillDiscountAppliedEventData = {
            bill: response,
        };

        if (props?.onDiscountAppliedToBill) {
            props?.onDiscountAppliedToBill(eventData);
        }
        emit("billDiscountApplied", eventData);
    }

    function reset() {
        voucherValue.value = undefined;
    }

    function setBillId(newBillId: string | undefined) {
        billId.value = newBillId;
        cartId.value = undefined;
    }

    function setCartId(newCartId: string | undefined) {
        cartId.value = newCartId;
        billId.value = undefined;
    }

    defineExpose({
        reset,
        setBillId,
        setCartId,
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .apply-discount-btn:not([disabled]) {
        .icon {
            background-color: colors.$green !important;
        }
    }

    .operator.apply-discount {
        display: flex;
        text-align: center;

        > .formrow {
            width: 100%;
            padding: 0 1.75em 10px !important;
        }

        > button {
            display: inline-block;
            vertical-align: top;
            background-color: transparent;
            border: none;
            padding: 4px;
            font-size: 24px;
            margin: -4px 0 -4px 0px;

            > .icon {
                width: 1.75em;
                height: 1.75em;
                background-color: var(--colorPrimary);
                border-radius: 50%;
                color: colors.$white;
                font-size: 16px;

                > i {
                    line-height: 1.75em;
                }
            }

            &.disabled,
            &[disabled] {
                cursor: default;

                > .icon {
                    background-color: colors.$lightGrey !important;
                    color: colors.$white !important;
                }
            }
        }
    }

    // this block sizes the row in the app: the .apply-discount block in app.scss uses
    // `.inputs-container > .apply-discount`, and the section is mounted in a wrapper div,
    // so that direct-child selector never matches here
    .app > .pay {
        .apply-discount {
            display: flex;
            // position: absolute;
            // bottom: 380px;
            // left: 0;
            // right: 0;
            height: 65px;
            text-align: center;
            padding: 0 26px;

            > .formrow {
                width: 100%;
            }

            > button {
                display: inline-block;
                vertical-align: top;
                background-color: transparent;
                border: none;
                padding: 4px;
                font-size: 24px;
                margin: -4px 0 -4px 0px;

                > .icon {
                    width: 1.75em;
                    height: 1.75em;
                    background-color: var(--colorPrimary);
                    border-radius: 50%;
                    color: colors.$white;

                    font-size: 16px;

                    > i {
                        line-height: 1.75em;
                    }
                }

                &.disabled,
                &[disabled] {
                    cursor: default;

                    > .icon {
                        background-color: colors.$lightGrey !important;
                        color: colors.$white !important;
                    }
                }
            }
        }
    }

    // kiosk: the row is sized in px, so its height, its padding, the input, the label in
    // both states and the round buttons all have to scale together. the section is mounted
    // in a wrapper div inside .inputs-container, hence the descendant combinators --
    // .inputs-container is also what outranks the base row in app.scss, whatever order the
    // stylesheets end up in
    .kiosk-mode .app > .pay .inputs-container .apply-discount {
        height: 65px * variables.$kioskScale;
        padding: 0 0;

        .formcontrol {
            padding: (14px * variables.$kioskScale) 0 (2px * variables.$kioskScale) 0;

            > label {
                font-size: 24px * variables.$kioskScale;
                top: 24px * variables.$kioskScale;
            }

            // the label shrinks and rises once the field has content, so both
            // positions have to move or the transition lands wrong
            &.editing,
            &.edited {
                > label {
                    font-size: 19px * variables.$kioskScale;
                    top: -6px * variables.$kioskScale;
                }
            }

            input {
                height: 42px * variables.$kioskScale;
                font-size: 24px * variables.$kioskScale;
            }
        }

        > button {
            padding: 12px * variables.$kioskScale;
            margin: (-12px * variables.$kioskScale) 0 (-12px * variables.$kioskScale) 0;

            // the circle is 1.75em wide and the glyph inherits the size, so this scales both
            > .icon {
                font-size: 26px * variables.$kioskScale;
            }
        }
    }
</style>
